import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Modal, notification, Tooltip } from 'antd';
import {
    DashboardOutlined, TeamOutlined, SafetyCertificateOutlined,
    MailOutlined, CreditCardOutlined, StarOutlined, GlobalOutlined,
    LogoutOutlined, MenuOutlined, UserOutlined, CloseOutlined, SettingOutlined,
} from '@ant-design/icons';
import { useAdminAuth } from '../../context/AdminAuthContext';
import adminApi from '../../api/adminAxios';
import '../../admin.css';

const navItems = [
    {
        section: 'Main',
        items: [
            { path: '/admin/dashboard',          label: 'Dashboard',          icon: <DashboardOutlined /> },
            { path: '/admin/users',               label: 'Users',              icon: <TeamOutlined /> },
            { path: '/admin/verifications',       label: 'Verifications',      icon: <SafetyCertificateOutlined /> },
        ],
    },
    {
        section: 'Content',
        items: [
            { path: '/admin/email-templates',     label: 'Email Templates',    icon: <MailOutlined /> },
            { path: '/admin/plans',               label: 'Plans',              icon: <StarOutlined /> },
            { path: '/admin/brand-partnership',   label: 'Brand Partnership',  icon: <GlobalOutlined /> },
        ],
    },
    {
        section: 'Finance',
        items: [
            { path: '/admin/payments',            label: 'Payments',           icon: <CreditCardOutlined /> },
        ],
    },
    {
        section: 'Account',
        items: [
            { path: '/admin/settings',            label: 'Settings',           icon: <SettingOutlined /> },
        ],
    },
];

const pageLabels = {
    '/admin/dashboard':        'Dashboard',
    '/admin/users':            'Users',
    '/admin/verifications':    'Verifications',
    '/admin/email-templates':  'Email Templates',
    '/admin/plans':            'Plans',
    '/admin/brand-partnership':'Brand Partnership',
    '/admin/payments':         'Payments',
    '/admin/settings':         'Settings',
};

export default function AdminLayout({ children, title }) {
    const { admin, logout } = useAdminAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);
    const [logoutModalOpen, setLogoutModalOpen] = useState(false);

    const currentLabel = title || pageLabels[location.pathname] || 'Admin';

    const handleLogout = async () => {
        setLoggingOut(true);
        try {
            await adminApi.post('/admin/logout');
        } catch (_) {}
        logout();
        notification.success({ message: 'Logged out successfully.', description: 'You have been logged out of the admin portal.' });
        navigate('/admin/login');
        setLoggingOut(false);
    };

    const initials = admin?.name
        ? admin.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
        : 'A';

    const closeSidebar = () => setSidebarOpen(false);

    return (
        <div className="admin-root">
            {/* Overlay (mobile) */}
            <div
                className={`admin-sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
                onClick={closeSidebar}
            />

            {/* Sidebar */}
            <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="admin-sidebar-logo">
                    <div className="admin-sidebar-logo-icon">
                        <UserOutlined />
                    </div>
                    <div className="admin-sidebar-logo-text">
                        <span>Oottan</span>
                        <span>Admin Portal</span>
                    </div>
                    <button
                        onClick={closeSidebar}
                        style={{
                            marginLeft: 'auto', background: 'none', border: 'none',
                            color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
                            fontSize: 16, padding: 4,
                        }}
                        className="admin-sidebar-close"
                    >
                        <CloseOutlined />
                    </button>
                </div>

                <nav className="admin-sidebar-nav">
                    {navItems.map((section) => (
                        <div key={section.section} className="admin-nav-section">
                            <div className="admin-nav-section-label">{section.section}</div>
                            {section.items.map((item) => {
                                const isActive = location.pathname === item.path ||
                                    location.pathname.startsWith(item.path + '/');
                                return (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        className={`admin-nav-item ${isActive ? 'active' : ''}`}
                                        onClick={closeSidebar}
                                    >
                                        <span className="admin-nav-item-icon">{item.icon}</span>
                                        {item.label}
                                    </Link>
                                );
                            })}
                        </div>
                    ))}
                </nav>

                <div className="admin-sidebar-footer">
                    <div className="admin-sidebar-user">
                        <div className="admin-sidebar-user-avatar">{initials}</div>
                        <div className="admin-sidebar-user-info">
                            <div className="admin-sidebar-user-name">{admin?.name || 'Admin'}</div>
                            <div className="admin-sidebar-user-role">
                                {(admin?.role || 'admin').replace('_', ' ')}
                            </div>
                        </div>
                        <Tooltip title="Logout">
                            <Button
                                type="text"
                                icon={<LogoutOutlined />}
                                onClick={() => setLogoutModalOpen(true)}
                                loading={loggingOut}
                                style={{ color: 'rgba(255,255,255,0.6)', minWidth: 0, padding: 4 }}
                            />
                        </Tooltip>
                    </div>
                </div>
            </aside>

            {/* Main */}
            <main className="admin-main">
                <header className="admin-header">
                    <div className="admin-header-left">
                        <button className="admin-hamburger" onClick={() => setSidebarOpen(true)}>
                            <MenuOutlined />
                        </button>
                        <span className="admin-header-title">{currentLabel}</span>
                    </div>
                    <div className="admin-header-right">
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.3 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                                {admin?.name || 'Admin'}
                            </span>
                            <span className="admin-header-email" style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                                {admin?.email}
                            </span>
                        </div>
                        <Button
                            type="text"
                            icon={<LogoutOutlined />}
                            onClick={() => setLogoutModalOpen(true)}
                            loading={loggingOut}
                            style={{ color: 'var(--color-text-secondary)' }}
                        >
                            <span className="admin-header-logout-label">Logout</span>
                        </Button>
                    </div>
                </header>

                <div className="admin-content">{children}</div>
            </main>

            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: 'rgba(239,68,68,0.1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <LogoutOutlined style={{ color: '#EF4444', fontSize: 16 }} />
                        </div>
                        <span style={{ fontWeight: 600 }}>Confirm Logout</span>
                    </div>
                }
                open={logoutModalOpen}
                onOk={handleLogout}
                onCancel={() => setLogoutModalOpen(false)}
                okText="Yes, Logout"
                cancelText="Stay"
                centered
                okButtonProps={{
                    danger: true,
                    loading: loggingOut,
                    style: { borderRadius: 8, fontWeight: 600, height: 36 },
                }}
                cancelButtonProps={{ style: { borderRadius: 8, height: 36 } }}
            >
                <p style={{ color: '#64748B', margin: '8px 0' }}>
                    Are you sure you want to logout from the Admin Portal?
                </p>
            </Modal>
        </div>
    );
}
